import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { ShoppingCart, CreditCard, Package, Truck, BarChart, Settings } from 'lucide-react';
import PageTransition from '../../components/ui/PageTransition';
import HeroSection from '../../components/sections/HeroSection';
import SectionHeading from '../../components/ui/SectionHeading';
import CtaSection from '../../components/sections/CtaSection';
import Button from '../../components/ui/Button';

const EcommercePage = () => {
  return (
    <PageTransition>
      <Helmet>
        <title>E-commerce Development Services | Consiliuma - Nottingham Web Experts</title>
        <meta name="description" content="Expert e-commerce website development in Nottingham. Build a powerful online store that drives sales with our comprehensive e-commerce solutions. British expertise with global reach." />
        <meta name="keywords" content="E-commerce Development Nottingham, Online Store Development, E-commerce Website Design, Shopping Cart Development, E-commerce Solutions UK" />
        <link rel="canonical" href="/services/ecommerce" />
      </Helmet>

      <HeroSection
        title="E-commerce Development Services"
        subtitle="Build a powerful online store that drives sales and growth"
        ctaText="Start Your E-commerce Project"
        ctaLink="/quote"
        backgroundImage="https://images.pexels.com/photos/230544/pexels-photo-230544.jpeg"
      />

      {/* Core Features */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <SectionHeading
            title="Comprehensive E-commerce Solutions"
            subtitle="Everything you need to succeed in online retail"
            center
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <ShoppingCart size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">Custom Store Design</h3>
              <p className="text-secondary">
                Beautiful, branded online stores that showcase your products effectively and drive conversions.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <CreditCard size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">Secure Payments</h3>
              <p className="text-secondary">
                Integration with trusted payment gateways to ensure secure and smooth transactions.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <Package size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">Inventory Management</h3>
              <p className="text-secondary">
                Robust systems to track stock levels, manage orders, and streamline fulfillment.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <Truck size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">Shipping Integration</h3>
              <p className="text-secondary">
                Seamless integration with shipping providers and real-time shipping calculations.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <BarChart size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">Analytics & Reporting</h3>
              <p className="text-secondary">
                Detailed insights into sales, customer behavior, and store performance.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <Settings size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">Store Management</h3>
              <p className="text-secondary">
                User-friendly admin interface for easy product, order, and customer management.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* E-commerce Features */}
      <section className="py-16 md:py-24 bg-background-alt">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <SectionHeading
                title="Advanced E-commerce Features"
                subtitle="Everything you need for online retail success"
              />
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <span className="font-bold">1</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-2">Product Management</h3>
                    <p className="text-secondary">
                      Easy product uploads, variants, categories, and bulk management tools.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <span className="font-bold">2</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-2">Customer Accounts</h3>
                    <p className="text-secondary">
                      Secure customer accounts with order history and saved preferences.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <span className="font-bold">3</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-2">Discount Systems</h3>
                    <p className="text-secondary">
                      Flexible coupon and discount management for promotions.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <span className="font-bold">4</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-2">Tax Management</h3>
                    <p className="text-secondary">
                      Automated tax calculations and compliance for different regions.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <span className="font-bold">5</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-2">Marketing Tools</h3>
                    <p className="text-secondary">
                      Built-in SEO, email marketing, and social media integration.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <Button to="/quote" variant="primary">
                  Start Your E-commerce Project
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <img 
                src="https://images.pexels.com/photos/230544/pexels-photo-230544.jpeg"
                alt="E-commerce features"
                className="rounded-lg shadow-lg"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Platform Options */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <SectionHeading
            title="E-commerce Platforms"
            subtitle="Choose the right platform for your business"
            center
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            <motion.div
              className="bg-white p-6 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-bold mb-3 text-primary">WooCommerce</h3>
              <p className="text-secondary mb-4">
                Perfect for WordPress-based stores with complete customization control.
              </p>
              <ul className="space-y-2 text-secondary">
                <li>• Flexible product options</li>
                <li>• Extensive plugin ecosystem</li>
                <li>• Full customization control</li>
                <li>• Self-hosted solution</li>
              </ul>
            </motion.div>

            <motion.div
              className="bg-white p-6 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-bold mb-3 text-primary">Shopify</h3>
              <p className="text-secondary mb-4">
                Hosted solution ideal for quick launch and easy management.
              </p>
              <ul className="space-y-2 text-secondary">
                <li>• Quick setup</li>
                <li>• Built-in hosting</li>
                <li>• Secure payments</li>
                <li>• App marketplace</li>
              </ul>
            </motion.div>

            <motion.div
              className="bg-white p-6 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-bold mb-3 text-primary">Custom Solution</h3>
              <p className="text-secondary mb-4">
                Tailored e-commerce platform built specifically for your needs.
              </p>
              <ul className="space-y-2 text-secondary">
                <li>• Complete control</li>
                <li>• Unique features</li>
                <li>• Scalable architecture</li>
                <li>• Custom integrations</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CtaSection
        title="Ready to Launch Your Online Store?"
        subtitle="Let's create an e-commerce solution that drives growth"
        buttonText="Get Started"
        buttonLink="/quote"
      />
    </PageTransition>
  );
};

export default EcommercePage;